<template>
  <div
    id="performance"
    class="px-4"
  >
    <v-row
      key="performancePortalFilterWeekly"
      align="center"
      dense
      class="mb-4"
    >
      <v-spacer />
      <v-col
        cols="auto"
        class="pr-6"
      >
        <v-btn
          :text="weeklyStatsPlayers.type !== 'total'"
          class="text-none"
          :color="weeklyStatsPlayers.type === 'total' ? 'primary' : undefined"
          @click="weeklyStatsPlayers.type = 'total'"
        >
          {{ $t('performance.weekly.total') }}
        </v-btn>
        <v-btn
          :text="weeklyStatsPlayers.type !== 'games'"
          class="text-none"
          :color="weeklyStatsPlayers.type === 'games' ? 'primary' : undefined"
          @click="weeklyStatsPlayers.type = 'games'"
        >
          {{ $t('performance.weekly.compared') }}
        </v-btn>
        <v-btn
          :text="weeklyStatsPlayers.type !== 'games60'"
          class="text-none"
          :color="weeklyStatsPlayers.type === 'games60' ? 'primary' : undefined"
          @click="weeklyStatsPlayers.type = 'games60'"
        >
          {{ $t('performance.weekly.compared_60') }}
        </v-btn>
      </v-col>
      <v-col
        cols="auto"
        class="text-subtitle-1 grey--text pr-2"
      >
        {{ $t('picker.date.select_date.title') }}
      </v-col>
      <v-col cols="auto">
        <v-btn
          icon
          @click="date = $dayjs(date).subtract(1, 'day').format('YYYY-MM-DD')"
        >
          <v-icon small>
            fa fa-arrow-left
          </v-icon>
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-menu>
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="date ? $dayjs(date).format('ddd, ll') : ''"
              :label="$t('picker.date.title')"
              solo
              hide-details
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="date"
            :first-day-of-week="1"
            no-title
            :max="$dayjs().format('YYYY-MM-DD')"
          />
        </v-menu>
      </v-col>
      <v-col cols="auto">
        <v-btn
          icon
          :disabled="$dayjs().isSame($dayjs(date), 'date')"
          @click="date = $dayjs(date).add(1, 'day').format('YYYY-MM-DD')"
        >
          <v-icon small>
            fa fa-arrow-right
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-divider />

    <section
      class="performance__tables mb-6"
    >
      <v-card class="mt-6">
        <v-card-title class="px-6 py-4">
          <v-row
            no-gutters
            align="center"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('event_details.players') }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-text-field
                v-model="playerName"
                solo
                dense
                :label="$t('table_headers.name')"
                hide-details
                clearable
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerPositions.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.position')"
                hide-details
                clearable
                item-value="id"
                item-text="name"
                :items="playerPositions.data"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <v-select
                v-model="playerStatuses.selectedId"
                solo
                dense
                :label="$t('table_headers.player_overview.status')"
                hide-details
                clearable
                :items="playerStatuses.data"
                item-value="id"
                item-text="name"
                style="width: 200px"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <variable-selector
                v-if="variables.data"
                :variables="variables.data"
                :dashboard="DashboardType.WEEKLY_ANALYSIS"
                category="GPS"
              />
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text class="pa-0">
          <div
            v-if="variables.loading || (!weeklyStatsPlayers.data && weeklyStatsPlayers.loading)"
            class="pa-5 d-flex justify-center align-center"
            style="min-height: 300px"
          >
            <v-progress-circular
              indeterminate
              size="48"
              width="4"
              color="grey lighten-2"
            />
          </div>
          <div v-else>
            <performance-weekly-table
              v-if="weeklyStatsPlayers.data"
              :player-performance="filteredWeeklyStatsPlayers"
              :loading="weeklyStatsPlayers.loading"
              :max-values="weeklyStatsPlayers.maxValues"
              :type="weeklyStatsPlayers.type"
              :table-headers="tableHeadersGPS"
              :colors-max-values="colorMaxValuesWeekly"
            />
          </div>
        </v-card-text>
      </v-card>
    </section>

    <performance-weekly-graphs
      v-if="weeklyStatsPlayers.data"
      :data="weeklyStatsPlayers.data"
      :type="weeklyStatsPlayers.type"
    />

    <performance-coach-customize
      :visible.sync="customizeModal"
      :dashboard="dashboard"
      view="GPS"
      @save="fetchVariables"
    />
  </div>
</template>

<script>
import { CoachApiService, PlayerApiService, PlayerPositionApiService, PlayerStatusApiService } from '@/data/services'
import { getColorForValue, processMaxValues, toCamelCase } from '@/utils/helpers'
import dayjs from 'dayjs'
import PerformanceWeeklyTable from '@/components/performance/PerformanceWeeklyTable'
import debounce from 'lodash/debounce'
import PerformanceWeeklyGraphs from '@/components/performance/PerformanceWeeklyGraphs'
import { maxValuesPerformanceWeekly } from '@/data/models/TableHeader'
import { orderBy } from 'lodash'
import { EventBus } from '@/plugins/bus'
import PerformanceCoachCustomize from '@/components/performance/PerfomanceCoachCustomize.vue'
import { DashboardType } from '@/data/models/Dashboard'
import VariableSelector from '@/components/performance/VariableSelector.vue'

export default {
  name: 'PerformanceWeeklyView',
  // eslint-disable-next-line vue/no-unused-components
  components: {
    VariableSelector,
    PerformanceCoachCustomize,
    PerformanceWeeklyGraphs,
    PerformanceWeeklyTable
  },
  data: () => ({
    date: dayjs().format('YYYY-MM-DD'),
    weeklyStatsPlayers: {
      type: 'total',
      loading: false,
      data: null,
      maxValues: {}
    },
    playerStatuses: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    playerPositions: {
      data: [],
      loading: false,
      loaded: false,
      selectedId: undefined
    },
    variables: {
      data: null,
      loading: false
    },
    playerName: '',
    colorMaxValuesWeekly: maxValuesPerformanceWeekly,
    allPlayers: {
      loading: false,
      data: []
    },
    customizeModal: false,
    dashboard: DashboardType.WEEKLY_ANALYSIS
  }),
  computed: {
    DashboardType() {
      return DashboardType
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    filteredWeeklyStatsPlayers() {
      if (!this.weeklyStatsPlayers.data) return []

      return this.weeklyStatsPlayers.data.filter(player => {
        if (this.playerPositions.selectedId) return player.player_positions.map(p => p.id).indexOf(this.playerPositions.selectedId) > -1

        return player
      }).filter(player => {
        if (this.playerStatuses.selectedId) return player.player_status_id === this.playerStatuses.selectedId

        return player
      }).filter(player => {
        if (this.playerName) return (player.first_name + player.last_name).toLowerCase().includes(this.playerName.toLowerCase())

        return player
      })
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    tableHeadersGPS() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.GPS.filter(tableHeader => tableHeader.selected), ['order'])) : []
    }
  },
  watch: {
    currentTeam() {
      this.fetchPlayers()
    },
    date() {
      this.fetchPlayers()
    }
  },
  created() {
    this.fetchVariables()

    this.fetchPlayers()
    this.fetchPlayerPositions()
    this.fetchPlayerStatuses()

    EventBus.$on(this.$events.INJURY_CREATED, () => {
      this.fetchPlayers()
    })
    EventBus.$on(this.$events.INJURY_UPDATED, () => {
      this.fetchPlayers()
    })
    EventBus.$on(this.$events.INJURY_DELETED, () => {
      this.fetchPlayers()
    })
  },
  methods: {
    orderBy,
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    fetchPlayers: debounce(async function () {
      try {
        this.weeklyStatsPlayers.loading = true

        const queryParams = {
          team_id: this.currentTeam.id === 'all' ? undefined : this.currentTeam.id,
          stats_date: this.date
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data.map(player => {
          if (!player.latest_stats) {
            return player
          }
          return player
        })

        this.weeklyStatsPlayers.maxValues = processMaxValues(players.map(player => player.latest_stats), 1.1)

        this.weeklyStatsPlayers.data = players
      } catch (e) {
        this.weeklyStatsPlayers.data = []
        throw e
      } finally {
        this.weeklyStatsPlayers.loading = false
      }
    }, 200),
    async fetchPlayerPositions() {
      if (this.playerPositions.loaded) return

      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll()

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      if (this.playerStatuses.loaded) return

      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        this.variables.data = await CoachApiService.getVariables(this.user.coach.id, {
          dashboard: this.dashboard
        })

        Object.keys(this.variables.data).forEach(group => {
          Object.keys(this.variables.data[group]).forEach(category => {
            this.variables.data[group][category] = this.variables.data[group][category].map(v => {
              return {
                ...v,
                text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
                long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
              }
            })
          })
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    }
  }
}
</script>

<style scoped>
</style>
